import { useEffect, useState } from 'react';
import { Clear } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import type { ScorerKind, Source } from '@store/generated/eval-results';

export type TriState = 'any' | 'true' | 'false';

export interface EvalResultFilters {
  organizationId?: string;
  patientId?: string;
  feature?: string;
  scorerName?: string;
  source?: Source;
  scorerKind?: ScorerKind;
  passed?: TriState;
  reviewed?: TriState;
  since?: string;
  until?: string;
}

interface EvalResultFiltersProps {
  filters: EvalResultFilters;
  onChange: (filters: EvalResultFilters) => void;
  onClear: () => void;
}

const SOURCE_OPTIONS: Source[] = ['realtime', 'cli', 'regression'];
const SCORER_KIND_OPTIONS: ScorerKind[] = ['check', 'judge'];

function isFilterActive(filters: EvalResultFilters): number {
  return Object.entries(filters).filter(([, v]) => {
    if (v === undefined || v === '') return false;
    if (v === 'any') return false;
    return true;
  }).length;
}

export function EvalResultFiltersBar({ filters, onChange, onClear }: EvalResultFiltersProps) {
  const [local, setLocal] = useState<EvalResultFilters>(filters);

  useEffect(() => {
    setLocal(filters);
  }, [filters]);

  const update = <K extends keyof EvalResultFilters>(key: K, value: EvalResultFilters[K]) => {
    const next = { ...local, [key]: value || undefined };
    setLocal(next);
    onChange(next);
  };

  const activeCount = isFilterActive(filters);

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Filters</Typography>
          {activeCount > 0 && (
            <Button size="small" startIcon={<Clear />} onClick={onClear} variant="outlined">
              Clear All
            </Button>
          )}
        </Box>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Organization ID"
              size="small"
              value={local.organizationId || ''}
              onChange={e => update('organizationId', e.target.value)}
              placeholder="org_..."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Patient ID"
              size="small"
              value={local.patientId || ''}
              onChange={e => update('patientId', e.target.value)}
              placeholder="patient_..."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Feature"
              size="small"
              value={local.feature || ''}
              onChange={e => update('feature', e.target.value)}
              placeholder="e.g. patient_intelligence"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              label="Scorer Name"
              size="small"
              value={local.scorerName || ''}
              onChange={e => update('scorerName', e.target.value)}
              placeholder="e.g. non_empty_content (filters visible page)"
              helperText="Client-side filter on this page only"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="source-label">Source</InputLabel>
              <Select
                labelId="source-label"
                label="Source"
                value={local.source ?? 'any'}
                onChange={e => {
                  const v = e.target.value;
                  update('source', v === 'any' ? undefined : v);
                }}
              >
                <MenuItem value="any">Any</MenuItem>
                {SOURCE_OPTIONS.map(s => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="scorer-kind-label">Scorer Kind</InputLabel>
              <Select
                labelId="scorer-kind-label"
                label="Scorer Kind"
                value={local.scorerKind ?? 'any'}
                onChange={e => {
                  const v = e.target.value;
                  update('scorerKind', v === 'any' ? undefined : v);
                }}
              >
                <MenuItem value="any">Any</MenuItem>
                {SCORER_KIND_OPTIONS.map(k => (
                  <MenuItem key={k} value={k}>
                    {k}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="passed-label">Passed</InputLabel>
              <Select
                labelId="passed-label"
                label="Passed"
                value={local.passed || 'any'}
                onChange={e => update('passed', e.target.value)}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="true">Pass</MenuItem>
                <MenuItem value="false">Fail</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="reviewed-label">Reviewed</InputLabel>
              <Select
                labelId="reviewed-label"
                label="Reviewed"
                value={local.reviewed || 'any'}
                onChange={e => update('reviewed', e.target.value)}
              >
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="true">Reviewed</MenuItem>
                <MenuItem value="false">Unreviewed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="date"
              label="Since"
              size="small"
              value={local.since || ''}
              onChange={e => update('since', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth
              type="date"
              label="Until"
              size="small"
              value={local.until || ''}
              onChange={e => update('until', e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
